import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Youtube, Save, Trash2, ExternalLink } from 'lucide-react';
import { API } from './adminUtils';

const extractYouTubeId = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
};

const formatDate = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

const ReplaysTab = () => {
  const [matches, setMatches] = useState([]);
  const [edits, setEdits] = useState({}); // matchId -> url
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    fetchMatches();
  }, []);

  const fetchMatches = async () => {
    try {
      const res = await axios.get(`${API}/admin/matches`);
      const sorted = (res.data || []).sort((a, b) => new Date(b.date) - new Date(a.date));
      setMatches(sorted);
      const initial = {};
      sorted.forEach(m => { initial[m.id] = m.youtube_url || ''; });
      setEdits(initial);
    } catch (err) {
      console.error('Erro ao carregar partidas:', err);
    }
  };

  const handleSave = async (matchId) => {
    setSaving(matchId);
    try {
      await axios.put(`${API}/admin/matches/replay`, {
        match_id: matchId,
        youtube_url: edits[matchId] || '',
      });
      fetchMatches();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao guardar');
    }
    setSaving(null);
  };

  const handleClear = async (matchId) => {
    if (!window.confirm('Remover o link do YouTube desta partida?')) return;
    setEdits({ ...edits, [matchId]: '' });
    setSaving(matchId);
    try {
      await axios.put(`${API}/admin/matches/replay`, { match_id: matchId, youtube_url: '' });
      fetchMatches();
    } catch {
      alert('Erro ao remover');
    }
    setSaving(null);
  };

  return (
    <div className="card">
      <h2 className="text-3xl font-bold mb-2 flex items-center gap-2">
        <Youtube size={28} className="text-red-600" /> REPLAYS DO YOUTUBE
      </h2>
      <p className="text-sm text-zinc-500 mb-6">
        Adiciona um link do YouTube em cada partida. Os links aparecem automaticamente na página pública <code className="bg-zinc-100 px-1 rounded">/replays</code>.
      </p>

      {matches.length === 0 ? (
        <p className="text-zinc-400 text-center py-6">Nenhuma partida criada.</p>
      ) : (
        <div className="space-y-3">
          {matches.map((m) => {
            const url = edits[m.id] ?? '';
            const valid = !url || extractYouTubeId(url);
            const hasReplay = !!m.youtube_url;
            return (
              <div
                key={m.id}
                className={`border-2 ${hasReplay ? 'border-green-500 bg-green-50' : 'border-black'} rounded-lg p-4`}
                data-testid={`replay-match-${m.id}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                  <div>
                    <p className="font-black text-lg">{m.location || 'Sem local'}</p>
                    <p className="text-sm text-zinc-600">{formatDate(m.date)} • {m.time || ''}</p>
                  </div>
                  {hasReplay && (
                    <a
                      href={m.youtube_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-bold text-red-600 hover:text-red-800 inline-flex items-center gap-1"
                    >
                      <ExternalLink size={14} /> Ver no YouTube
                    </a>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    placeholder="https://youtube.com/watch?v=…  ou  https://youtu.be/…"
                    value={url}
                    onChange={(e) => setEdits({ ...edits, [m.id]: e.target.value })}
                    className={`input-field flex-1 ${!valid ? 'border-red-500' : ''}`}
                    data-testid={`replay-input-${m.id}`}
                  />
                  <button
                    onClick={() => handleSave(m.id)}
                    disabled={!valid || saving === m.id}
                    className="btn-primary text-sm py-2 px-4"
                    data-testid={`replay-save-${m.id}`}
                  >
                    <Save size={16} className="inline mr-1" />
                    {saving === m.id ? 'A GUARDAR…' : 'GUARDAR'}
                  </button>
                  {hasReplay && (
                    <button
                      onClick={() => handleClear(m.id)}
                      className="btn-secondary text-sm py-2 px-4"
                      data-testid={`replay-clear-${m.id}`}
                    >
                      <Trash2 size={16} className="inline mr-1" /> REMOVER
                    </button>
                  )}
                </div>
                {!valid && (
                  <p className="text-xs text-red-600 font-bold mt-1">URL do YouTube inválido</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ReplaysTab;
